import { useCallback, useMemo, useState } from "react";
import { TableReportSellersColumnsDefinition } from "./components/TableReportSellersColumnsDefinition";
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { useReportSellers } from "@/app/hooks/useReportSellers";
import * as XLSX from "xlsx";
import {
  ReportSellers,
  ReportSellersParams,
} from "@/app/services/reportService/reportSellers";
import { reportService } from "@/app/services/reportService";
import { FiltersType } from "../components/FilterReports";
import { useAuth } from "@/app/hooks/useAuth";

const AMOUNT_REPORTS_FETCH = Number(import.meta.env.VITE_AMOUNT_REPORTS_FETCH);

export const useReportSellersController = () => {
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const { data, isFetching, isLoading, setParams, params } = useReportSellers();
  const { isAdmin, isManager, isSupervisor, isLeader } = useAuth();

  const { columns } = TableReportSellersColumnsDefinition();

  const table = useReactTable({
    data: data?.sellers || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: data?.pagination.limit || 100,
      },
    },
  });

  const fetchAllData = useCallback(async (): Promise<ReportSellers[]> => {
    if (!data) return [];

    setIsExporting(true);
    setExportProgress(0);

    const batchSize = AMOUNT_REPORTS_FETCH;
    const total = data.pagination.total;
    let currentSkip = 0;
    let allData: ReportSellers[] = [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const batches: Promise<any>[] = [];

    let completedBatches = 0;

    while (total > currentSkip) {
      const exportParams: ReportSellersParams = {
        ...params,
        limit: batchSize,
        skip: currentSkip,
      };

      const batchPromise = reportService
        .reportSellers(exportParams)
        .then((batchData) => {
          allData = [...allData, ...batchData.sellers];

          completedBatches += 1;

          const progress = Number(
            Math.min(
              ((completedBatches * batchSize) / total) * 100,
              100,
            ).toFixed(0),
          );
          setExportProgress(progress);
        })
        .catch((error) => {
          console.error("Error fetching batch data:", error);
        });

      batches.push(batchPromise);

      currentSkip += batchSize;
    }

    try {
      await Promise.all(batches);
    } catch (error) {
      console.error("Error awaiting batch resolutions:", error);
    }

    setIsExporting(false);
    return allData;
  }, [data, params]);

  const handleExportToExcel = useCallback(async () => {
    setIsExporting(true);
    const fetchedData = await fetchAllData();

    const formattedData = fetchedData.map((seller) => ({
      cpf: seller.cpf || "",
      name: seller.name || "",
      email: seller.email || "",
      phone: seller.phone || "",
      instagram: seller.instagram || "",
      team: seller.team_name || "Sem equipe",
      manager: seller.manager || "Sem gerente",
      supervisor: seller.supervisor || "Sem supervisor",
      leader: seller.leader || "Sem líder",
      transactions: seller.transactions || 0,
      withdraw: seller.withdraw || 0,
      commission: +(seller.commission / 100).toFixed(2) || 0,
      withdrawValue: +(seller.withdraw_value / 100).toFixed(2) || 0,
      transactionValue: +(seller.transaction_value / 100).toFixed(2) || 0,
      date: format(seller.createdAt, "dd/MM/yyyy, HH:mm") || "",
    }));

    setIsExporting(false);

    if (formattedData.length > 0) {
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "ExportedReport");
      XLSX.utils.sheet_add_aoa(worksheet, [
        [
          "CPF",
          "Nome",
          "Email",
          "Telefone",
          "Instagram",
          "Equipe",
          "Gerente",
          "Supervisor",
          "lider",
          "Nº Transações",
          "Nº Saques",
          "Total em comissões",
          "Total em saques",
          "Total movimentado",
          "Data de Cadastro",
        ],
      ]);
      XLSX.writeFile(workbook, "sellers-reports.xlsx");
    }
  }, [fetchAllData]);

  const getPageIndex = () => {
    if (!data) return 0;
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1;
  };

  const getPageCount = () => {
    if (!data) return 0;
    return Math.ceil(data.pagination.total / data.pagination.limit);
  };

  const handleNextPage = () => {
    if (!data) return;
    const newPage = data.pagination.skip / data.pagination.limit + 1;
    if (getPageCount() === getPageIndex()) return;
    const skipValue = newPage * data.pagination.limit;
    setParams((p) => ({ ...p, skip: skipValue }));
  };

  const handlePreviousPage = () => {
    if (!data) return;
    const newPage = data.pagination.skip / data.pagination.limit - 1;
    if (newPage < 0) return;
    const skipValue = newPage * data.pagination.limit;
    setParams((p) => ({ ...p, skip: skipValue }));
  };

  const filters: FiltersType[] = useMemo(() => {
    const result: FiltersType[] = ["Date"];

    if (isAdmin)
      result.push("Managers", "Supervisors", "Leaders", "Teams", "Sellers");
    if (isManager) result.push("Supervisors", "Leaders", "Teams", "Sellers");
    if (isSupervisor) result.push("Leaders", "Teams", "Sellers");
    if (isLeader) result.push("Sellers");

    result.push("Value");

    return result;
  }, [isAdmin, isManager, isSupervisor]);

  return {
    table,
    filters,
    handleNextPage,
    handlePreviousPage,
    getPageIndex,
    getPageCount,
    isFetching,
    isLoading,
    setParams,
    isExporting,
    exportProgress,
    handleExportToExcel,
  };
};
